import { observer } from 'mobx-react-lite';
import { useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { mergeQueryString, updateSearch } from 'bernie-core';

import { ESSAdapter, TypeaheadSelection, UitkTypeahead } from 'uitk-react-typeahead';

import { ActivityDestinationViewSearch, ActivityGroupViewType } from '../../common/__generated__/api';

import { useStores } from '../../utils/hooks';

import { getPageId } from '../../helpers/common-helper';

import { getUpdatedFilterQueryObject } from '../srp/sort-filter/filter-helper';
import { parse } from 'query-string';

const NOT_FOUND_INDEX = -1;

export interface ActivityTypeaheadSearchInputProps {
  isCarousel?: boolean;
  maxTypeaheadResults?: number;
}

export const ActivityTypeaheadSearchInput: React.FC<ActivityTypeaheadSearchInputProps> = observer(
  ({ isCarousel, maxTypeaheadResults }) => {
    const { analytics, activitySearchStore, context } = useStores();
    const history = useHistory();
    const location = useLocation();
    const tileIndex = (activitySearchStore.activityGroups as ActivityDestinationViewSearch.ActivityGroups[]).findIndex(
      (activityGroup) => activityGroup.viewType === ActivityGroupViewType.KEYWORD_SEARCH
    );
    let keywordData = {
      analytics: {
        linkName: 'keyword-search-done',
        referrerId: `${getPageId(activitySearchStore)}.filterSort.SearchActivity`,
      },
      placeholder: activitySearchStore.sortAndFilterObject.keywordPlaceholder,
      keyword: activitySearchStore.sortAndFilterObject.keywordValue,
    };

    if (isCarousel && tileIndex > NOT_FOUND_INDEX) {
      keywordData = {
        analytics: activitySearchStore.activityGroups[tileIndex].activityTiles[0].analytics,
        placeholder: activitySearchStore.activityGroups[tileIndex].activityTiles[0].placeholder || '',
        keyword: activitySearchStore.activityGroups[tileIndex].activityTiles[0].searchTerm || '',
      };
    }

    const updateTypeahead = (typeaheadResult: TypeaheadSelection): void | null => {
      const filterKeyWord = typeaheadResult.term;
      keywordData.keyword = filterKeyWord;
      activitySearchStore.sortAndFilterObject.keywordValue = filterKeyWord;
      const newQueryString = mergeQueryString(location.search, {
        slimSearchKeyword: filterKeyWord,
      });
      if (typeaheadResult.data) {
        analytics.trackEvent(
          `SR.filterSort.SearchActivity.${filterKeyWord}`,
          'searchActivity-typeahead-search-done',
          analytics.omnitureData,
          null,
          true
        );
      }
      if (isCarousel) {
        updateSearch({
          history: history,
          location: location,
          newParams: newQueryString,
          newState: {},
          replace: true,
        });
      }
    };

    const handleClear = (): void => {
      if (isCarousel) {
        const newQueryString = mergeQueryString(location.search, {
          ...getUpdatedFilterQueryObject(activitySearchStore),
          sort: activitySearchStore.sortAndFilterObject.initialSelectedSort,
          slimSearchKeyword: '',
          seeAll: activitySearchStore.sortAndFilterObject.initialSeeAllOption,
          pwaDialog: '',
        });
        updateSearch({
          history: history,
          location: location,
          newParams: newQueryString,
          newState: {},
          replace: true,
        });
      } else {
        activitySearchStore.sortAndFilterObject.keywordValue = '';
      }
    };

    const queryParams = parse(location.search);
    const rid = queryParams && queryParams.rid ? parseInt(queryParams.rid, 10) : undefined;

    const activityTypeaheadAdapter = {
      adapter: new ESSAdapter({
        client: 'LXActivitiesSearchPage',
        dest: true,
        features: '',
        guid: context && context.deviceId,
        lob: 'ACTIVITIES',
        locale: context && context.locale,
        maxResults: maxTypeaheadResults || 10,
        regionType: 2048,
        siteId: context && context.site.id,
        regionId: rid,
      }),
      label: keywordData.placeholder,
      typeaheadPlaceholder: keywordData.placeholder,
    };

    return (
      <UitkTypeahead
        adapter={activityTypeaheadAdapter.adapter}
        allowExactMatch={true}
        icon="search"
        label={activityTypeaheadAdapter.label}
        name="searchKeyWord"
        onUpdate={updateTypeahead}
        stid="typeahead-adaptive"
        data-testid="typeahead-adaptive"
        handleClear={handleClear}
        hasClearButton={true}
        value={keywordData.keyword}
        typeaheadPlaceholder={activityTypeaheadAdapter.typeaheadPlaceholder}
      />
    );
  }
);
